import { useEffect, useMemo, useRef, useState } from 'react'
import { addOperation, updateOperation, getCategories, getComptes } from '../services/api'
import { useToast } from './ToastProvider'
import { categoryIcon } from '../utils/categoryIcon'
import { todayISO, shiftDaysISO, formatLocalISO } from '../utils/dates'
import { parseAmount } from '../utils/parseAmount'
import Icon from './Icon'

const EMPTY_FORM = {
  type: 'entrée',
  montant: '',
  description: '',
  categorie: '',
  date: todayISO(),
  compte_id: '',
}

const DESCRIPTION_MAX = 500

export default function OperationModal({ operation, categories: initialCategories, onClose, onSave }) {
  const toast = useToast()
  const [categories, setCategories] = useState(initialCategories || [])
  const [comptes, setComptes] = useState([])
  const [comptesStatus, setComptesStatus] = useState('loading')
  const [formData, setFormData] = useState(EMPTY_FORM)
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const montantRef = useRef(null)
  const dialogRef = useRef(null)

  useEffect(() => {
    setCategories(initialCategories || [])
  }, [initialCategories])

  useEffect(() => {
    let cancelled = false
    setComptesStatus('loading')
    getComptes(true)
      .then((list) => {
        if (cancelled) return
        setComptes(list)
        setComptesStatus('ready')
      })
      .catch(() => {
        if (cancelled) return
        setComptes([])
        setComptesStatus('error')
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (operation) {
      setFormData({
        type: operation.type,
        montant: String(operation.montant ?? ''),
        description: operation.description || '',
        categorie: operation.categorie || '',
        date: formatLocalISO(operation.date),
        compte_id: operation.compte_id != null ? String(operation.compte_id) : '',
      })
    } else {
      setFormData({ ...EMPTY_FORM, date: todayISO() })
    }
    setErrors({})
  }, [operation])

  // Présélection du compte une fois la liste chargée : un seul compte ne mérite
  // pas un choix. Séparé de la remise à zéro pour ne pas effacer une saisie en
  // cours si la liste arrive après que l'utilisateur a commencé à taper.
  useEffect(() => {
    if (operation || comptes.length !== 1) return
    setFormData((current) => (current.compte_id ? current : { ...current, compte_id: String(comptes[0].id) }))
  }, [comptes, operation])

  useEffect(() => {
    const handleCategoriesChange = async () => {
      try {
        setCategories(await getCategories())
      } catch (error) {
        console.error('Erreur lors du rechargement des catégories:', error)
      }
    }
    window.addEventListener('categoriesChanged', handleCategoriesChange)
    return () => window.removeEventListener('categoriesChanged', handleCategoriesChange)
  }, [])

  // Le focus part sur le montant : c'est le champ qu'on vient remplir dans la
  // quasi-totalité des cas. Effet séparé et sans dépendance, donc exécuté au
  // seul montage : regroupé avec l'écoute du clavier ci-dessous, il se
  // rejouait à chaque ouverture de la liste des catégories et ramenait le
  // focus sur le montant dès qu'on cliquait dans le champ Catégorie.
  useEffect(() => {
    montantRef.current?.focus()
  }, [])

  // Échap ferme la fenêtre — ou d'abord la liste des suggestions si elle est ouverte.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return
      if (showCategoryDropdown) {
        setShowCategoryDropdown(false)
        return
      }
      if (!submitting) onClose?.()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose, submitting, showCategoryDropdown])

  const categoryNames = useMemo(
    () => (categories || []).map((cat) => (typeof cat === 'object' ? cat.nom : cat)).filter(Boolean),
    [categories]
  )

  const suggestions = useMemo(() => {
    const filter = formData.categorie.trim().toLowerCase()
    if (!filter) return categoryNames
    return categoryNames.filter((name) => name.toLowerCase().includes(filter))
  }, [categoryNames, formData.categorie])

  const isNewCategory =
    formData.categorie.trim() !== '' &&
    !categoryNames.some((name) => name.toLowerCase() === formData.categorie.trim().toLowerCase())

  const setField = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }))
    // L'erreur disparaît dès que l'utilisateur corrige, sans attendre la soumission.
    setErrors((current) => (current[field] ? { ...current, [field]: undefined } : current))
  }

  /** Valide champ par champ : le message doit s'afficher sous le champ fautif, pas dans une alerte. */
  const validate = () => {
    const nextErrors = {}
    const montant = parseAmount(formData.montant)
    if (formData.montant === '' || Number.isNaN(montant)) {
      nextErrors.montant = 'Saisissez un montant'
    } else if (montant < 0.01) {
      nextErrors.montant = 'Le montant doit être d’au moins 0,01'
    } else if (montant > 999999999.99) {
      nextErrors.montant = 'Montant trop élevé'
    }
    if (!formData.categorie.trim()) {
      nextErrors.categorie = 'Choisissez ou créez une catégorie'
    } else if (formData.categorie.trim().length > 100) {
      nextErrors.categorie = 'Le nom de la catégorie est trop long (100 caractères max.)'
    }
    if (!formData.date) {
      nextErrors.date = 'Choisissez une date'
    }
    if (comptesStatus === 'loading') {
      nextErrors.compte_id = 'Chargement des comptes…'
    } else if (comptesStatus === 'error') {
      nextErrors.compte_id = 'Impossible de charger les comptes. Réessayez.'
    } else if (comptes.length === 0) {
      nextErrors.compte_id = 'Créez d’abord un compte pour enregistrer une opération'
    } else if (!formData.compte_id) {
      nextErrors.compte_id = 'Sélectionnez un compte'
    }
    if (formData.description.length > DESCRIPTION_MAX) {
      nextErrors.description = `Description trop longue (${DESCRIPTION_MAX} caractères max.)`
    }
    setErrors(nextErrors)
    return nextErrors
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (submitting) return

    if (comptesStatus !== 'ready') return

    const nextErrors = validate()
    const firstError = Object.keys(nextErrors)[0]
    if (firstError) {
      document.getElementById(`operation-${firstError}`)?.focus()
      return
    }

    setSubmitting(true)
    try {
      const payload = {
        ...formData,
        categorie: formData.categorie.trim(),
        description: formData.description.trim(),
        montant: parseAmount(formData.montant),
        compte_id: formData.compte_id ? parseInt(formData.compte_id, 10) : null,
      }

      const response = operation
        ? await updateOperation(operation.id, payload)
        : await addOperation(payload)

      if (response?.category_created) {
        try {
          setCategories(await getCategories())
        } catch (error) {
          console.error('Erreur lors du rechargement des catégories:', error)
        }
        window.dispatchEvent(new Event('categoriesChanged'))
      }

      window.dispatchEvent(new Event('operationChanged'))
      toast.success(
        operation
          ? 'Opération modifiée'
          : `Opération ajoutée${response?.category_created ? ` · catégorie « ${payload.categorie} » créée` : ''}`
      )

      await onSave?.()
      onClose?.()
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error)
      const data = error.response?.data
      if (data?.errors && typeof data.errors === 'object') {
        // Remonter les erreurs du serveur sur les champs concernés.
        const serverErrors = {}
        Object.entries(data.errors).forEach(([field, messages]) => {
          serverErrors[field] = Array.isArray(messages) ? messages.join(', ') : String(messages)
        })
        setErrors(serverErrors)
        toast.error('Certains champs sont invalides')
      } else {
        toast.error(data?.message || data?.error || error.message || 'Erreur lors de l’enregistrement')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleCategoryKeyDown = (e) => {
    if (!showCategoryDropdown || suggestions.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((i) => (i + 1) % suggestions.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1))
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault()
      setField('categorie', suggestions[highlightedIndex])
      setShowCategoryDropdown(false)
      setHighlightedIndex(-1)
    }
  }

  const fieldClass = (field) => `input ${errors[field] ? 'input-invalid' : ''}`

  const FieldError = ({ field }) =>
    errors[field] ? (
      <p id={`operation-${field}-error`} role="alert" className="field-error">
        {errors[field]}
      </p>
    ) : null

  return (
    <div className="modal-backdrop" onClick={() => !submitting && onClose?.()} role="presentation">
      <div
        ref={dialogRef}
        className="modal-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="operation-modal-title"
      >
        <div className="modal-header">
          <h3 id="operation-modal-title" className="modal-title">
            {operation ? 'Modifier une opération' : 'Ajouter une opération'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="btn-icon -mr-1.5"
            aria-label="Fermer"
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} noValidate className="flex min-h-0 flex-1 flex-col">
          <div className="modal-body space-y-4">
            {/* Choix binaire : deux boutons valent mieux qu'une liste déroulante. */}
            <div className="field">
              <span className="field-label">Type</span>
              <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Type d’opération">
                {[
                  { value: 'entrée', label: 'Entrée', icon: 'arrowDown' },
                  { value: 'dépense', label: 'Dépense', icon: 'arrowUp' },
                ].map((option) => {
                  const selected = formData.type === option.value
                  const positif = option.value === 'entrée'
                  return (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => setField('type', option.value)}
                      // Sélection marquée par un contour teinté plutôt qu'un aplat
                      // plein : le formulaire garde une seule action principale,
                      // le bouton d'enregistrement.
                      className={`inline-flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-base font-medium transition-colors duration-fast ${
                        selected
                          ? positif
                            ? 'border-positive bg-positive-subtle text-positive-on-subtle'
                            : 'border-negative bg-negative-subtle text-negative-on-subtle'
                          : 'border-line-strong bg-surface text-ink-secondary hover:bg-surface-hover'
                      }`}
                    >
                      <Icon
                        name={option.icon}
                        size={15}
                        className={selected ? '' : positif ? 'text-positive' : 'text-negative'}
                      />
                      {option.label}
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="field">
              <label htmlFor="operation-montant" className="field-label">Montant</label>
              <input
                id="operation-montant"
                ref={montantRef}
                type="number"
                step="0.01"
                min="0.01"
                inputMode="decimal"
                placeholder="0,00"
                value={formData.montant}
                onChange={(e) => setField('montant', e.target.value)}
                aria-invalid={!!errors.montant}
                aria-describedby={errors.montant ? 'operation-montant-error' : undefined}
                className={`${fieldClass('montant')} tnum text-right text-lg`}
              />
              <FieldError field="montant" />
            </div>

            <div className="field">
              <label htmlFor="operation-compte_id" className="field-label">Compte</label>
              {comptesStatus === 'loading' ? (
                <p className="field-hint">Chargement des comptes…</p>
              ) : comptesStatus === 'error' ? (
                <p id="operation-compte_id-error" role="alert" className="field-error">
                  Impossible de charger les comptes. Fermez puis rouvrez cette fenêtre.
                </p>
              ) : comptes.length === 0 ? (
                <p id="operation-compte_id-error" role="alert" className="field-error">
                  Créez d’abord un compte pour enregistrer une opération.
                </p>
              ) : (
                <select
                  id="operation-compte_id"
                  value={formData.compte_id}
                  onChange={(e) => setField('compte_id', e.target.value)}
                  aria-invalid={!!errors.compte_id}
                  aria-describedby={errors.compte_id ? 'operation-compte_id-error' : undefined}
                  className={`select ${errors.compte_id ? 'input-invalid' : ''}`}
                >
                  <option value="">Sélectionnez un compte</option>
                  {comptes.map((c) => (
                    <option key={c.id} value={c.id}>{c.nom}</option>
                  ))}
                </select>
              )}
              {comptesStatus === 'ready' && comptes.length > 0 && <FieldError field="compte_id" />}
            </div>

            <div className="field">
              <label htmlFor="operation-categorie" className="field-label">Catégorie</label>
              <div className="relative">
                <input
                  id="operation-categorie"
                  type="text"
                  autoComplete="off"
                  role="combobox"
                  aria-expanded={showCategoryDropdown}
                  aria-autocomplete="list"
                  aria-invalid={!!errors.categorie}
                  aria-describedby={errors.categorie ? 'operation-categorie-error' : undefined}
                  value={formData.categorie}
                  onChange={(e) => {
                    setField('categorie', e.target.value)
                    setShowCategoryDropdown(true)
                    setHighlightedIndex(-1)
                  }}
                  onFocus={() => setShowCategoryDropdown(true)}
                  onBlur={() => setTimeout(() => setShowCategoryDropdown(false), 150)}
                  onKeyDown={handleCategoryKeyDown}
                  placeholder="Saisissez ou sélectionnez une catégorie"
                  className={`${fieldClass('categorie')} pr-9`}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center px-2.5 text-ink-muted"
                  onClick={() => setShowCategoryDropdown((open) => !open)}
                  aria-label={showCategoryDropdown ? 'Masquer les catégories' : 'Afficher les catégories'}
                  tabIndex={-1}
                >
                  <Icon
                    name="chevronDown"
                    size={16}
                    className={`transition-transform duration-fast ${showCategoryDropdown ? 'rotate-180' : ''}`}
                  />
                </button>
                {showCategoryDropdown && (
                  <ul
                    role="listbox"
                    className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-md border border-line bg-surface p-1 shadow-lg"
                  >
                    {suggestions.map((name, index) => (
                      <li
                        key={name}
                        role="option"
                        aria-selected={index === highlightedIndex}
                        className={`flex cursor-pointer items-center gap-2.5 rounded-sm px-2.5 py-2 text-base ${
                          index === highlightedIndex
                            ? 'bg-accent-subtle text-accent-on-subtle'
                            : 'text-ink-secondary hover:bg-surface-hover'
                        }`}
                        onMouseEnter={() => setHighlightedIndex(index)}
                        onMouseDown={(e) => {
                          e.preventDefault()
                          setField('categorie', name)
                          setShowCategoryDropdown(false)
                          setHighlightedIndex(-1)
                        }}
                      >
                        <Icon name={categoryIcon(name, formData.type)} size={15} className="text-ink-muted" />
                        {name}
                      </li>
                    ))}
                    {suggestions.length === 0 && (
                      <li className="px-2.5 py-2 text-sm text-ink-muted">
                        Aucune catégorie existante ne correspond
                      </li>
                    )}
                  </ul>
                )}
              </div>
              <FieldError field="categorie" />
              {/* Annoncer la création avant l'enregistrement, pas après. */}
              {!errors.categorie && isNewCategory && (
                <p className="flex items-center gap-1.5 text-xs text-accent">
                  <Icon name="plus" size={13} />
                  Nouvelle catégorie « {formData.categorie.trim()} » : elle sera créée à l’enregistrement.
                </p>
              )}
            </div>

            <div className="field">
              <label htmlFor="operation-date" className="field-label">Date</label>
              <input
                id="operation-date"
                type="date"
                value={formData.date}
                onChange={(e) => setField('date', e.target.value)}
                aria-invalid={!!errors.date}
                aria-describedby={errors.date ? 'operation-date-error' : undefined}
                className={fieldClass('date')}
              />
              <div className="mt-0.5 flex flex-wrap gap-1.5">
                {[
                  { label: 'Aujourd’hui', value: todayISO() },
                  { label: 'Hier', value: shiftDaysISO(-1) },
                  { label: 'Avant-hier', value: shiftDaysISO(-2) },
                ].map((shortcut) => (
                  <button
                    key={shortcut.label}
                    type="button"
                    onClick={() => setField('date', shortcut.value)}
                    className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors duration-fast ${
                      formData.date === shortcut.value
                        ? 'border-accent bg-accent-subtle text-accent-on-subtle'
                        : 'border-line text-ink-secondary hover:bg-surface-hover'
                    }`}
                  >
                    {shortcut.label}
                  </button>
                ))}
              </div>
              <FieldError field="date" />
            </div>

            <div className="field">
              <label htmlFor="operation-description" className="field-label">
                Description <span className="font-normal text-ink-muted">(optionnel)</span>
              </label>
              <input
                id="operation-description"
                type="text"
                maxLength={DESCRIPTION_MAX}
                value={formData.description}
                onChange={(e) => setField('description', e.target.value)}
                aria-invalid={!!errors.description}
                className={fieldClass('description')}
                placeholder="Courses du samedi"
              />
              <div className="flex items-start justify-between gap-2">
                <FieldError field="description" />
                {formData.description.length > DESCRIPTION_MAX - 100 && (
                  <span className="field-hint tnum shrink-0">
                    {formData.description.length}/{DESCRIPTION_MAX}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" onClick={onClose} disabled={submitting} className="btn-secondary flex-1">
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting || comptesStatus !== 'ready' || comptes.length === 0}
              className="btn-primary flex-1"
            >
              {submitting && <Icon name="refresh" size={15} className="animate-spin" />}
              {submitting ? 'Enregistrement…' : operation ? 'Enregistrer' : 'Ajouter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
